import { getDefaultModelConfig } from "@/3d/modelConfig/getDefaultModelConfig";
import { MeshStats } from "@/3d/modelConfig/MeshStats";
import { loadModelConfig, ModelConfig } from "@/3d/modelConfig/ModelConfig";
import { MtlParser } from "@/3d/rawModel/MtlParser";
import { ObjParser } from "@/3d/rawModel/ObjParser";
import { RawModel } from "@/3d/rawModel/RawModel";
import { isNotEmpty, isNotNil } from "@/utils/typeGuards";
import { isEmpty } from "lodash";
import { FileLoader } from "three/src/loaders/FileLoader";
import { TextureLoader } from "three/src/loaders/TextureLoader";

export type ModelWithConfig = {
    raw: RawModel;
    config: ModelConfig;
};

export const loadModelWithConfig = async (config: {
    modelPath: string;
    configPath: string;
}): Promise<ModelWithConfig | undefined> => {
    const loader = new FileLoader();

    let raw: RawModel | undefined;
    try {
        raw = await loadModel(config.modelPath, loader);
    } catch (e) {
        console.error("Failed to load model", { config }, e);
        return undefined;
    }

    if (!raw) {
        console.error("raw model not found");
        return undefined;
    }

    await loadModelMaterials(raw, config.modelPath, loader);

    const modelConfig = !isEmpty(config.configPath)
        ? await loadModelConfig(config.configPath)
        : getDefaultModelConfig(raw);

    if (!modelConfig) {
        console.error("model config not found");
        return undefined;
    }

    const meshStats: Record<string, MeshStats> = {};

    raw.meshes.forEach((mesh) => {
        meshStats[mesh.name] = new MeshStats(mesh, modelConfig.meshConfig[mesh.name]);
    });

    raw.meshStats = meshStats;

    return { raw, config: modelConfig };
};

export const loadModel = async (
    modelPath: string,
    loader: FileLoader
): Promise<RawModel | undefined> => {
    const content = await loader.loadAsync(modelPath);
    if (typeof content !== "string") {
        console.error("content is not string");
        return undefined;
    }

    const parser = new ObjParser();
    const raw = parser.parse(content);
    if (raw.meshes.length === 0) {
        console.error("model contains no meshes", { modelPath });
        return undefined;
    }

    raw.computeBoundingRect();

    return raw;
};

export const loadModelMaterials = async (raw: RawModel, modelPath: string, loader: FileLoader) => {
    const modelDirectory = modelPath.substring(0, modelPath.lastIndexOf("/"));
    const materialLibraryPath = modelDirectory + "/" + raw.materialLibraryPath;
    const materialDefinitions = await loadMaterialLibrary(materialLibraryPath, loader);

    if (!materialDefinitions) {
        console.error("Failed to load material definitions", {
            modelDirectory,
            materialLibraryPath,
        });
        return;
    }

    raw.materialDefinitions = materialDefinitions;

    const textureLoader = new TextureLoader();

    for (const materialDefinition of raw.materialDefinitions) {
        const loadMaterial = async (
            prop:
                | "diffuseMap"
                | "specularColorMap"
                | "specularHighlightMap"
                | "bumpMap"
                | "displacementMap"
                | "metalnessMap"
                | "roughnessMap"
        ) => {
            if (isNotNil(materialDefinition[prop]) && isNotEmpty(materialDefinition[prop])) {
                try {
                    return await textureLoader.loadAsync(
                        modelDirectory + "/" + materialDefinition[prop]
                    );
                } catch (e) {
                    console.error("Failed to load material texture", {
                        modelDirectory,
                        materialDefinition,
                        prop,
                    });
                    return undefined;
                }
            }

            return undefined;
        };

        const colorMap = await loadMaterial("diffuseMap");
        const roughnessMap = await loadMaterial("roughnessMap");
        const metalnessMap = await loadMaterial("metalnessMap");
        const normalMap = await loadMaterial("bumpMap");

        raw.materials.push({
            definition: materialDefinition,
            textures: {
                color: colorMap,
                roughness: roughnessMap,
                normal: normalMap,
                displacement: undefined,
                metalness: metalnessMap,
            },
        });
    }
};

const loadMaterialLibrary = async (path: string, loader: FileLoader) => {
    try {
        const content = await loader.loadAsync(path);
        if (typeof content !== "string") {
            console.error("content is not string");
            return undefined;
        }

        const materialDefinitions = MtlParser.parse(content);

        return materialDefinitions;
    } catch (e) {
        console.error("Failed to load material library", { path }, e);
        return undefined;
    }
};
